import { useState, useEffect } from 'react';
import { UserStatus } from '@artham/shared';
import { usePathname } from 'src/routes/hooks';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { CONFIG } from 'src/global-config';

import { SplashScreen } from 'src/components/loading-screen';

import { useAuthContext } from '../hooks';
import { AuthErrorDialog } from '../view/auth-error-dialog';
import { UserStatusDialog } from '../view/user-status-dialog';
import { UserProfileCreationView } from '../view/user-profile-creation-view';

// ----------------------------------------------------------------------

type AuthGuardProps = {
  children: React.ReactNode;
};

const signInPaths = {
  jwt: paths.auth.jwt.signIn,
  auth0: paths.auth.auth0.signIn,
  amplify: paths.auth.amplify.signIn,
  firebase: paths.auth.firebase.signIn,
  supabase: paths.auth.supabase.signIn,
};

export function AuthGuard({ children }: AuthGuardProps) {
  const router = useRouter();
  const pathname = usePathname();

  const { authenticated, loading, needsProfile, userStatus, error } = useAuthContext();

  const [isChecking, setIsChecking] = useState(true);

  const checkPermissions = async (): Promise<void> => {
    if (loading) {
      return;
    }

    if (!authenticated && !error) {
      const { method } = CONFIG.auth;

      const signInPath = signInPaths[method];

      router.replace(signInPath);

      return;
    }

    setIsChecking(false);
  };

  useEffect(() => {
    checkPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, loading, error]);

  if (isChecking) {
    return <SplashScreen />;
  }

  // Show error dialog if there's an API error
  if (error) {
    return <AuthErrorDialog error={error} />;
  }

  // Show profile creation if user is authenticated but needs to create profile
  if (needsProfile) {
    return <UserProfileCreationView mode="create" />;
  }

  // Block access for KYC_PENDING or DISABLED users - show only status dialog.
  // Exception: allow the KYC screens through so users can actually fix + resubmit.
  const isKycRoute = pathname?.startsWith(paths.dashboard.artham.kyc.root);
  if (userStatus === UserStatus.KYC_PENDING && !isKycRoute) {
    return <UserStatusDialog status={userStatus} />;
  }
  if (userStatus === UserStatus.DISABLED) {
    return <UserStatusDialog status={userStatus} />;
  }

  // Only ACTIVE users can access the app
  return <>{children}</>;
}
